/**
 * Ámbito geográfico — sección 13b: UE-27 + Noruega, Islandia, Liechtenstein y Suiza (31).
 * Esta lista es la SEMILLA de `settings/countries`; en runtime manda Firestore (regla 6).
 * Idioma por defecto y cooficiales según la sección 13 (nunca forzados por país).
 */
import type { Currency } from './firestore/primitives.js';

export interface CountrySeed {
  code: string;
  name_en: string;
  default_language: string;
  languages: string[];
  currency: Currency;
  phone_prefix: string;
  eu_member: boolean;
  /** Envíos desde/hacia la UE con aduana (CN22/CN23). */
  customs_required: boolean;
  payment_methods: string[];
}

const CARD = ['card', 'apple_pay', 'google_pay'];

export const COUNTRY_SEED: readonly CountrySeed[] = [
  // ---- UE-27 ----
  {
    code: 'AT',
    name_en: 'Austria',
    default_language: 'de',
    languages: ['de'],
    currency: 'EUR',
    phone_prefix: '+43',
    eu_member: true,
    customs_required: false,
    payment_methods: [...CARD, 'eps'],
  },
  {
    code: 'BE',
    name_en: 'Belgium',
    default_language: 'nl',
    languages: ['nl', 'fr', 'de'],
    currency: 'EUR',
    phone_prefix: '+32',
    eu_member: true,
    customs_required: false,
    payment_methods: [...CARD, 'bancontact'],
  },
  {
    code: 'BG',
    name_en: 'Bulgaria',
    default_language: 'bg',
    languages: ['bg'],
    currency: 'BGN',
    phone_prefix: '+359',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'HR',
    name_en: 'Croatia',
    default_language: 'hr',
    languages: ['hr'],
    currency: 'EUR',
    phone_prefix: '+385',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'CY',
    name_en: 'Cyprus',
    default_language: 'el',
    languages: ['el', 'tr', 'en'],
    currency: 'EUR',
    phone_prefix: '+357',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'CZ',
    name_en: 'Czechia',
    default_language: 'cs',
    languages: ['cs'],
    currency: 'CZK',
    phone_prefix: '+420',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'DK',
    name_en: 'Denmark',
    default_language: 'da',
    languages: ['da'],
    currency: 'DKK',
    phone_prefix: '+45',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'EE',
    name_en: 'Estonia',
    default_language: 'et',
    languages: ['et'],
    currency: 'EUR',
    phone_prefix: '+372',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'FI',
    name_en: 'Finland',
    default_language: 'fi',
    languages: ['fi', 'sv'],
    currency: 'EUR',
    phone_prefix: '+358',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'FR',
    name_en: 'France',
    default_language: 'fr',
    languages: ['fr'],
    currency: 'EUR',
    phone_prefix: '+33',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'DE',
    name_en: 'Germany',
    default_language: 'de',
    languages: ['de'],
    currency: 'EUR',
    phone_prefix: '+49',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'GR',
    name_en: 'Greece',
    default_language: 'el',
    languages: ['el'],
    currency: 'EUR',
    phone_prefix: '+30',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'HU',
    name_en: 'Hungary',
    default_language: 'hu',
    languages: ['hu'],
    currency: 'HUF',
    phone_prefix: '+36',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'IE',
    name_en: 'Ireland',
    default_language: 'en',
    languages: ['en', 'ga'],
    currency: 'EUR',
    phone_prefix: '+353',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'IT',
    name_en: 'Italy',
    default_language: 'it',
    languages: ['it', 'de'],
    currency: 'EUR',
    phone_prefix: '+39',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'LV',
    name_en: 'Latvia',
    default_language: 'lv',
    languages: ['lv'],
    currency: 'EUR',
    phone_prefix: '+371',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'LT',
    name_en: 'Lithuania',
    default_language: 'lt',
    languages: ['lt'],
    currency: 'EUR',
    phone_prefix: '+370',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'LU',
    name_en: 'Luxembourg',
    default_language: 'fr',
    languages: ['fr', 'de', 'lb'],
    currency: 'EUR',
    phone_prefix: '+352',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'MT',
    name_en: 'Malta',
    default_language: 'mt',
    languages: ['mt', 'en'],
    currency: 'EUR',
    phone_prefix: '+356',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'NL',
    name_en: 'Netherlands',
    default_language: 'nl',
    languages: ['nl', 'fy'],
    currency: 'EUR',
    phone_prefix: '+31',
    eu_member: true,
    customs_required: false,
    payment_methods: [...CARD, 'ideal'],
  },
  {
    code: 'PL',
    name_en: 'Poland',
    default_language: 'pl',
    languages: ['pl'],
    currency: 'PLN',
    phone_prefix: '+48',
    eu_member: true,
    customs_required: false,
    payment_methods: [...CARD, 'blik', 'p24'],
  },
  {
    code: 'PT',
    name_en: 'Portugal',
    default_language: 'pt',
    languages: ['pt'],
    currency: 'EUR',
    phone_prefix: '+351',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'RO',
    name_en: 'Romania',
    default_language: 'ro',
    languages: ['ro', 'hu'],
    currency: 'RON',
    phone_prefix: '+40',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'SK',
    name_en: 'Slovakia',
    default_language: 'sk',
    languages: ['sk', 'hu'],
    currency: 'EUR',
    phone_prefix: '+421',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'SI',
    name_en: 'Slovenia',
    default_language: 'sl',
    languages: ['sl'],
    currency: 'EUR',
    phone_prefix: '+386',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'ES',
    name_en: 'Spain',
    default_language: 'es',
    languages: ['es', 'ca', 'eu', 'gl'],
    currency: 'EUR',
    phone_prefix: '+34',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  {
    code: 'SE',
    name_en: 'Sweden',
    default_language: 'sv',
    languages: ['sv'],
    currency: 'SEK',
    phone_prefix: '+46',
    eu_member: true,
    customs_required: false,
    payment_methods: CARD,
  },
  // ---- EEE / Schengen no UE (aduana) ----
  {
    code: 'NO',
    name_en: 'Norway',
    default_language: 'no',
    languages: ['no'],
    currency: 'NOK',
    phone_prefix: '+47',
    eu_member: false,
    customs_required: true,
    payment_methods: CARD,
  },
  {
    code: 'IS',
    name_en: 'Iceland',
    default_language: 'is',
    languages: ['is'],
    currency: 'ISK',
    phone_prefix: '+354',
    eu_member: false,
    customs_required: true,
    payment_methods: CARD,
  },
  {
    code: 'LI',
    name_en: 'Liechtenstein',
    default_language: 'de',
    languages: ['de'],
    currency: 'CHF',
    phone_prefix: '+423',
    eu_member: false,
    customs_required: true,
    payment_methods: CARD,
  },
  {
    code: 'CH',
    name_en: 'Switzerland',
    default_language: 'de',
    languages: ['de', 'fr', 'it', 'rm'],
    currency: 'CHF',
    phone_prefix: '+41',
    eu_member: false,
    customs_required: true,
    payment_methods: CARD,
  },
];

export const ALLOWED_COUNTRY_CODES: ReadonlySet<string> = new Set(COUNTRY_SEED.map((c) => c.code));

/** Moneda local de un país admitido (D6: la wallet y el checkout van en la moneda del usuario); EUR si no se sabe. */
export function currencyForCountry(code: string | null | undefined): Currency {
  const c = COUNTRY_SEED.find((r) => r.code === (code ?? '').toUpperCase());
  return c?.currency ?? 'EUR';
}

export function isAllowedCountry(code: string | null | undefined): boolean {
  return typeof code === 'string' && ALLOWED_COUNTRY_CODES.has(code.toUpperCase());
}

/** Prefijos permitidos para OTP y registro (13b). */
export const ALLOWED_PHONE_PREFIXES: readonly string[] = COUNTRY_SEED.map((c) => c.phone_prefix);

export function isAllowedPhone(e164: string): boolean {
  return ALLOWED_PHONE_PREFIXES.some((p) => e164.startsWith(p));
}

// ---------- 13b: qué puede hacer alguien según su país ----------

/** Lo mínimo de `settings/countries[]` que hace falta para decidir; en runtime viene de Firestore. */
export interface CountryAccessRow {
  code: string;
  signup_enabled: boolean;
  selling_enabled: boolean;
  buying_enabled: boolean;
  /** Prefijo E.164 del país (para validar el teléfono del registro). */
  phone_prefix?: string;
}

export interface CountryAccess {
  /** País consultado, en mayúsculas, o null si no se pudo determinar. */
  country: string | null;
  /** true si el país está en `settings/countries` (aunque tenga algo desactivado). */
  listed: boolean;
  signup: boolean;
  selling: boolean;
  buying: boolean;
}

/** Semilla como filas de acceso: todo abierto (lo mismo que siembra tools/seed). */
export const COUNTRY_ACCESS_SEED: readonly CountryAccessRow[] = COUNTRY_SEED.map((c) => ({
  code: c.code,
  signup_enabled: true,
  selling_enabled: true,
  buying_enabled: true,
  phone_prefix: c.phone_prefix,
}));

/**
 * Decide registro / venta / compra para un país. La LECTURA pública nunca pasa por aquí (12b):
 * esta función no tiene forma de decir "no puedes mirar".
 *
 * País desconocido (null, sin cabecera, IP no resuelta) → nada permitido por IP; quien llama decide
 * si eso bloquea (registro de invitado) o no (sesión ya verificada, VPN…), ver 13b.
 */
export function evaluateCountryAccess(
  code: string | null | undefined,
  rows: readonly CountryAccessRow[] = COUNTRY_ACCESS_SEED,
): CountryAccess {
  const country = typeof code === 'string' && code.trim() !== '' ? code.trim().toUpperCase() : null;
  const row = country === null ? undefined : rows.find((r) => r.code === country);
  if (!row) return { country, listed: false, signup: false, selling: false, buying: false };
  return {
    country,
    listed: true,
    signup: row.signup_enabled,
    selling: row.selling_enabled,
    buying: row.buying_enabled,
  };
}

/** País cuyo prefijo coincide con el teléfono (el más largo gana: +421 antes que +42…). */
export function countryForPhone(
  e164: string,
  rows: readonly CountryAccessRow[] = COUNTRY_ACCESS_SEED,
): string | null {
  let best: CountryAccessRow | null = null;
  for (const r of rows) {
    if (r.phone_prefix && e164.startsWith(r.phone_prefix)) {
      if (!best || r.phone_prefix.length > (best.phone_prefix?.length ?? 0)) best = r;
    }
  }
  return best?.code ?? null;
}
